import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND_SANS_FONT } from '../design/theme';
import { brandConfig } from '../design/theme';
import PublicRounded from '@mui/icons-material/PublicRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import ShowChartRounded from '@mui/icons-material/ShowChartRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import FolderSharedRounded from '@mui/icons-material/FolderSharedRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import CloudQueueRounded from '@mui/icons-material/CloudQueueRounded';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';

// =============================================================================
// "Connects To" artefact, shared by the landing, product, Excel/Word, and
// audience pages. Data sources sit in three labeled groups (left), flow
// through the Restack hub (center), and land on a real deliverable (right).
// Grouped-and-labeled replaced the old scattered-pills-on-a-dotted-globe:
// ordered rows read enterprise, scatter read like noise. Self contained:
// hardcoded to the light marketing surface (INK / ACCENT / white) rather than
// reading the MUI theme, exactly like the DeckVisuals family.
// =============================================================================

const INK = '#2E140A';
const ACCENT = '#B83B1D';

export default function ConnectorGlobe({ surface = 'powerpoint' }) {
  const ink = INK;
  const fam = BRAND_SANS_FONT;
  // Connectors as icon pills in three labeled groups. The live group leads;
  // green dot = available now, amber = on the roadmap.
  const GROUPS = [
    {
      label: 'Public web',
      items: [
        { name: 'Web Search', live: true, Icon: PublicRounded },
        { name: 'SEC EDGAR', live: false, Icon: PolicyRounded },
        { name: 'Company IR', live: true, Icon: DescriptionRounded },
        { name: 'News & Press', live: true, Icon: ArticleRounded },
      ],
    },
    {
      label: 'Market data',
      items: [
        { name: 'Bloomberg', live: false, Icon: QueryStatsRounded },
        { name: 'Capital IQ', live: false, Icon: AssessmentRounded },
        { name: 'PitchBook', live: false, Icon: TrendingUpRounded },
        { name: 'FactSet', live: false, Icon: BarChartRounded },
        { name: 'LSEG', live: false, Icon: ShowChartRounded },
        { name: 'S&P Global', live: false, Icon: InsightsRounded },
        { name: 'Crunchbase', live: false, Icon: AssessmentRounded },
      ],
    },
    {
      label: 'Documents & drives',
      items: [
        { name: 'SharePoint', live: false, Icon: FolderSharedRounded },
        { name: 'Box', live: false, Icon: StorageRounded },
        { name: 'OneDrive', live: false, Icon: CloudQueueRounded },
        { name: 'Google Drive', live: false, Icon: CloudQueueRounded },
        { name: 'Confluence', live: false, Icon: DescriptionRounded },
        { name: 'Notion', live: false, Icon: EditNoteRounded },
      ],
    },
  ];
  // Flat list for the desktop sphere: live pills lead, so they land on the
  // upper spots of the globe where the eye starts.
  const SOURCES = GROUPS.flatMap((g) => g.items);
  // Dotted sphere ("globe of connectors"). Fibonacci points projected
  // orthographically onto the front hemisphere so they pack denser toward the
  // rim and read as a 3D sphere. Deterministic (no random).
  const DOT_COUNT = 1400;
  const GOLDEN = Math.PI * (3 - Math.sqrt(5));
  const SPHERE_DOTS = [];
  for (let i = 0; i < DOT_COUNT; i++) {
    const y = 1 - (i / (DOT_COUNT - 1)) * 2;
    const ringR = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * GOLDEN;
    const z = Math.sin(theta) * ringR;
    if (z < -0.03) continue; // front hemisphere only
    const x = Math.cos(theta) * ringR;
    SPHERE_DOTS.push({
      cx: 50 + x * 47,
      cy: 50 - y * 47,
      o: 0.12 + Math.max(0, z) * 0.12,
    });
  }
  // Pill positions across the sphere face (% of the square frame), loosely
  // staggered so they read as scattered rather than gridded. Order follows
  // SOURCES (live group first, top of the sphere). Edge spots sit a touch
  // inside the rim so no pill reads as falling off the globe.
  const SPOTS = [
    { l: 28, t: 15 }, { l: 51, t: 11 }, { l: 73, t: 18 },
    { l: 16, t: 32 }, { l: 40, t: 29 }, { l: 63, t: 33 }, { l: 83, t: 39 },
    { l: 26, t: 48 }, { l: 50, t: 52 }, { l: 74, t: 49 }, { l: 16, t: 63 },
    { l: 39, t: 67 }, { l: 84, t: 61 }, { l: 64, t: 70 },
    { l: 30, t: 83 }, { l: 79, t: 80 }, { l: 53, t: 88 },
  ];
  // Animated data-flow dots that travel globe -> Restack -> deliverable, in
  // the container's 1180x540 pixel space so they stay round.
  const FLOW = [
    { p: 'M460.2,216 C507.4,216 519.2,270 542.8,270', dur: 1.9, begin: 0 },
    { p: 'M460.2,270 L542.8,270', dur: 1.9, begin: 0.55 },
    { p: 'M460.2,324 C507.4,324 519.2,270 542.8,270', dur: 1.9, begin: 1.1 },
    { p: 'M637.2,270 C672.6,270 672.6,178.2 708,178.2', dur: 1.8, begin: 0.8 },
    { p: 'M637.2,270 C672.6,270 672.6,243 708,243', dur: 1.8, begin: 1.35 },
    { p: 'M637.2,270 C672.6,270 672.6,302.4 708,302.4', dur: 1.8, begin: 0.45 },
    { p: 'M637.2,270 C672.6,270 672.6,361.8 708,361.8', dur: 1.8, begin: 1.7 },
  ];
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Surface-aware output node. Home / product / audience pages keep the
  // polished PowerPoint slide PNG (default). The Excel and Word product pages
  // pass surface="excel" / "word" so the connector beat resolves to the
  // surface the page is actually about (a model / a document), not a deck.
  const OUT_RADIUS = '14px';
  const surfaceCfg =
    {
      powerpoint: { caption: 'Your slide, on your template, with citations', alt: 'Restack building a market-sizing slide with a chart and cited sources' },
      excel: { caption: 'Your model, formula-driven, every input cited', alt: 'Restack building a Nordic market-sizing model in Excel with cited inputs' },
      word: { caption: 'Your document, on your template, with citations', alt: 'Restack drafting a Nordic market-sizing memo in Word with cited sources' },
    }[surface] || { caption: 'Your slide, on your template, with citations', alt: 'Restack building a market-sizing slide with a chart and cited sources' };

  const ExcelOutput = () => (
    <Box sx={{ width: '100%', borderRadius: OUT_RADIUS, overflow: 'hidden', border: `1px solid ${alpha(ink, 0.1)}`, boxShadow: '0 14px 32px rgba(46, 20, 10, 0.14)', backgroundColor: '#FFFFFF', fontFamily: fam }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 1, backgroundColor: '#217346' }}>
        <Box sx={{ width: 18, height: 18, borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, color: '#FFFFFF' }}>X</Box>
        <Typography sx={{ fontFamily: fam, fontSize: '0.78rem', fontWeight: 600, color: '#FFFFFF' }}>Nordic_Market_Sizing.xlsx</Typography>
        <Box component="span" sx={{ ml: 'auto', px: 0.8, py: 0.25, borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.18)', fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.06em', color: '#FFFFFF' }}>EXCEL</Box>
      </Box>
      <Box sx={{ p: 1.8 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 0.8fr', border: `1px solid ${alpha(ink, 0.1)}`, borderRadius: '8px', overflow: 'hidden', fontSize: '0.74rem', fontFamily: fam }}>
          {['Segment', '2026E', 'CAGR'].map((h, i) => (
            <Box key={`h${i}`} sx={{ px: 1.1, py: 0.65, backgroundColor: alpha('#217346', 0.09), fontWeight: 700, color: ink, borderBottom: `1px solid ${alpha(ink, 0.1)}`, textAlign: i === 0 ? 'left' : 'right' }}>{h}</Box>
          ))}
          {[['TAM', '$4.2B', '41%'], ['SAM', '$1.8B', '38%'], ['SOM', '$340M', '41%']].map((r, ri) =>
            r.map((c, ci) => (
              <Box key={`${ri}-${ci}`} sx={{ px: 1.1, py: 0.6, color: ci === 0 ? alpha(ink, 0.9) : '#217346', fontWeight: 600, textAlign: ci === 0 ? 'left' : 'right', borderBottom: ri < 2 ? `1px solid ${alpha(ink, 0.07)}` : 'none', backgroundColor: ci === 1 ? alpha('#217346', 0.04) : 'transparent' }}>{c}</Box>
            ))
          )}
        </Box>
        <Box sx={{ mt: 1.6, display: 'flex', alignItems: 'flex-end', gap: 0.8, height: 54 }}>
          {[44, 62, 80, 100].map((h, i) => (
            <Box key={i} sx={{ flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0', background: i === 3 ? '#217346' : alpha('#217346', 0.4) }} />
          ))}
        </Box>
        <Typography sx={{ mt: 1.1, fontFamily: fam, fontSize: '0.68rem', color: alpha(ink, 0.55) }}>Bottom-up model &middot; every input cited</Typography>
      </Box>
    </Box>
  );

  const WordOutput = () => (
    <Box sx={{ width: '100%', borderRadius: OUT_RADIUS, overflow: 'hidden', border: `1px solid ${alpha(ink, 0.1)}`, boxShadow: '0 14px 32px rgba(46, 20, 10, 0.14)', backgroundColor: '#FFFFFF', fontFamily: fam }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1.5, py: 1, backgroundColor: '#35548A' }}>
        <Box sx={{ width: 18, height: 18, borderRadius: '4px', backgroundColor: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, color: '#FFFFFF' }}>W</Box>
        <Typography sx={{ fontFamily: fam, fontSize: '0.78rem', fontWeight: 600, color: '#FFFFFF' }}>Nordic_Market_Sizing.docx</Typography>
        <Box component="span" sx={{ ml: 'auto', px: 0.8, py: 0.25, borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.18)', fontSize: '0.56rem', fontWeight: 700, letterSpacing: '0.06em', color: '#FFFFFF' }}>WORD</Box>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.98rem', fontWeight: 700, color: ink, mb: 1 }}>Investment Thesis</Typography>
        {[96, 100, 82].map((w, i) => (
          <Box key={i} sx={{ height: 6, width: `${w}%`, borderRadius: '3px', backgroundColor: alpha(ink, 0.09), mb: 0.85 }} />
        ))}
        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '0.82rem', lineHeight: 1.6, color: alpha(ink, 0.85), mt: 1.2 }}>
          Nordic payments TAM reaches{' '}
          <Box component="span" sx={{ color: '#C0392B', textDecoration: 'line-through' }}>$3.4B</Box>{' '}
          <Box component="span" sx={{ color: '#2B579A', fontWeight: 700, textDecoration: 'underline' }}>$4.2B by 2026</Box>.
        </Typography>
        <Typography sx={{ mt: 1.3, fontFamily: fam, fontSize: '0.68rem', color: alpha(ink, 0.55) }}>Tracked change &middot; every figure cited</Typography>
      </Box>
    </Box>
  );

  const OutputArt = ({ alt }) => {
    if (surface === 'excel') return <ExcelOutput />;
    if (surface === 'word') return <WordOutput />;
    return (
      <Box
        component="img"
        src="/connectors-output.png"
        alt={alt}
        loading="lazy"
        sx={{ width: '100%', display: 'block', borderRadius: OUT_RADIUS, border: `1px solid ${alpha(ink, 0.1)}`, boxShadow: '0 14px 32px rgba(46, 20, 10, 0.14)' }}
      />
    );
  };

  const Pill = ({ name, live, Icon, compact }) => (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 0.5 : 0.6,
        pl: compact ? 0.7 : 0.85,
        pr: compact ? 0.85 : 1.05,
        py: compact ? 0.4 : 0.55,
        borderRadius: '999px',
        border: `1px solid ${alpha(ink, 0.12)}`,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(46, 20, 10, 0.06)',
        fontFamily: fam,
        fontSize: compact ? '0.64rem' : '0.73rem',
        fontWeight: 500,
        color: alpha(ink, 0.82),
        whiteSpace: 'nowrap',
      }}
    >
      <Icon sx={{ fontSize: compact ? 12 : 14, color: alpha(ink, 0.5), flexShrink: 0 }} />
      {name}
      <Box
        component="span"
        sx={{ width: compact ? 5 : 6, height: compact ? 5 : 6, borderRadius: '50%', backgroundColor: live ? '#1B7A4F' : '#D4694E', flexShrink: 0, ml: 0.15 }}
      />
    </Box>
  );

  // Vertical animated connector for the mobile stack.
  const MobileFlow = () => (
    <Box
      component="svg"
      width="24"
      height="48"
      viewBox="0 0 24 48"
      sx={{ display: 'block', my: 0.25, overflow: 'visible', width: '24px', height: '48px', flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M12,3 L12,45" fill="none" stroke={alpha('#B83B1D', 0.3)} strokeWidth="1.1" strokeLinecap="round" strokeDasharray="0.1 4" />
      {!reduceMotion && [0, 0.9].map((b, i) => (
        <g key={i}>
          <circle r="3.2" fill={alpha('#B83B1D', 0.18)}>
            <animateMotion dur="1.8s" begin={`${b}s`} repeatCount="indefinite" path="M12,3 L12,45" calcMode="linear" />
            <animate attributeName="opacity" dur="1.8s" begin={`${b}s`} repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.15;0.75;1" />
          </circle>
          <circle r="1.7" fill="#B83B1D">
            <animateMotion dur="1.8s" begin={`${b}s`} repeatCount="indefinite" path="M12,3 L12,45" calcMode="linear" />
            <animate attributeName="opacity" dur="1.8s" begin={`${b}s`} repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.15;0.75;1" />
          </circle>
        </g>
      ))}
    </Box>
  );

  return (
    <>
      {/* DESKTOP - globe of connectors (left) flows through Restack (center)
          into your deliverable (right). */}
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', height: 540, maxWidth: 1180, mx: 'auto', mt: 2 }}>
        {/* dotted flow lines: globe -> Restack -> deliverable */}
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, overflow: 'visible' }}
          aria-hidden="true"
        >
          {[
            'M39,40 C43,40 44,50 46,50',
            'M39,50 L46,50',
            'M39,60 C43,60 44,50 46,50',
            'M54,50 C57,50 57,33 60,33',
            'M54,50 C57,50 57,45 60,45',
            'M54,50 C57,50 57,56 60,56',
            'M54,50 C57,50 57,67 60,67',
          ].map((d, i) => (
            <path key={i} d={d} fill="none" stroke={alpha('#B83B1D', 0.3)} strokeWidth="1.3" strokeLinecap="round" strokeDasharray="0.1 4" vectorEffect="non-scaling-stroke" />
          ))}
        </Box>

        {/* Animated data-flow dots riding the same lines. */}
        {!reduceMotion && (
          <Box
            component="svg"
            viewBox="0 0 1180 540"
            preserveAspectRatio="none"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, overflow: 'visible', pointerEvents: 'none' }}
            aria-hidden="true"
          >
            {FLOW.map((f, i) => (
              <g key={i}>
                <circle r="6.5" fill={alpha('#B83B1D', 0.18)}>
                  <animateMotion dur={`${f.dur}s`} begin={`${f.begin}s`} repeatCount="indefinite" path={f.p} calcMode="linear" />
                  <animate attributeName="opacity" dur={`${f.dur}s`} begin={`${f.begin}s`} repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.15;0.75;1" />
                </circle>
                <circle r="3.4" fill="#B83B1D">
                  <animateMotion dur={`${f.dur}s`} begin={`${f.begin}s`} repeatCount="indefinite" path={f.p} calcMode="linear" />
                  <animate attributeName="opacity" dur={`${f.dur}s`} begin={`${f.begin}s`} repeatCount="indefinite" values="0;1;1;0" keyTimes="0;0.15;0.75;1" />
                </circle>
              </g>
            ))}
          </Box>
        )}

        {/* LEFT - dotted globe with the connectors on it. Width scales with the
            container (capped at 480) and stays square via aspect-ratio, so the
            globe's right edge keeps clear of the centered hub at any container
            width. A fixed 480px width collided with the 50%-positioned hub on
            narrower wrappers (e.g. the 1100px audience pages). */}
        <Box sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '40%', maxWidth: 480, aspectRatio: '1 / 1', zIndex: 1 }}>
          <Box
            component="svg"
            viewBox="0 0 100 100"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            aria-hidden="true"
          >
            {SPHERE_DOTS.map((d, i) => (
              <circle key={i} cx={d.cx} cy={d.cy} r="0.34" fill={alpha(ink, d.o)} />
            ))}
          </Box>
          {SOURCES.map((s, i) => (
            <Box
              key={s.name}
              sx={{ position: 'absolute', left: `${SPOTS[i].l}%`, top: `${SPOTS[i].t}%`, transform: 'translate(-50%, -50%)', zIndex: 1 }}
            >
              <Pill name={s.name} live={s.live} Icon={s.Icon} compact />
            </Box>
          ))}
        </Box>

        {/* CENTER - Restack hub */}
        <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, width: 84, height: 84, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(184, 59, 29, 0.12) 0%, rgba(212, 105, 78, 0.12) 100%)', border: '1px solid rgba(184, 59, 29, 0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(184, 59, 29, 0.12)' }}>
          <brandConfig.icon sx={{ fontSize: 38, color: ACCENT }} />
        </Box>
        <Box sx={{ position: 'absolute', left: '50%', top: 'calc(50% + 56px)', transform: 'translateX(-50%)', zIndex: 2, textAlign: 'center', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: fam, fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.015em', color: ink }}>Restack</Typography>
          <Typography sx={{ fontFamily: fam, fontSize: '0.72rem', color: alpha(ink, 0.6) }}>Reads, verifies, cites</Typography>
        </Box>

        {/* RIGHT - your output (surface-aware: deck / model / document) */}
        <Box sx={{ position: 'absolute', left: '60%', top: '50%', transform: 'translateY(-50%)', width: '38%', zIndex: 1 }}>
          <OutputArt alt={surfaceCfg.alt} />
          <Typography sx={{ mt: 1, textAlign: 'center', fontFamily: fam, fontSize: '0.72rem', color: alpha(ink, 0.6) }}>
            {surfaceCfg.caption}
          </Typography>
        </Box>
      </Box>

      {/* MOBILE - clean vertical flow: grouped connector pills, then animated
          data dots down into Restack, then down into your deliverable. */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 460, mx: 'auto' }}>
          {GROUPS.map((g) => (
            <Box key={g.label} sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontFamily: fam,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: alpha(ink, 0.45),
                  mb: 0.75,
                }}
              >
                {g.label}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 0.85, rowGap: 1 }}>
                {g.items.map((s) => (
                  <Pill key={s.name} name={s.name} live={s.live} Icon={s.Icon} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <MobileFlow />

        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ width: 72, height: 72, mx: 'auto', mb: 1, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(184, 59, 29, 0.12) 0%, rgba(212, 105, 78, 0.12) 100%)', border: '1px solid rgba(184, 59, 29, 0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 18px rgba(184, 59, 29, 0.12)' }}>
            <brandConfig.icon sx={{ fontSize: 34, color: ACCENT }} />
          </Box>
          <Typography sx={{ fontFamily: fam, fontSize: '1rem', fontWeight: 700, color: ink }}>Restack</Typography>
          <Typography sx={{ fontFamily: fam, fontSize: '0.74rem', color: alpha(ink, 0.6) }}>Reads, verifies, cites</Typography>
        </Box>

        <MobileFlow />

        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <OutputArt alt={surfaceCfg.alt} />
          <Typography sx={{ mt: 0.5, textAlign: 'center', fontFamily: fam, fontSize: '0.72rem', color: alpha(ink, 0.6) }}>
            {surfaceCfg.caption}
          </Typography>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: { xs: 2.5, md: 1 }, maxWidth: 1180, mx: 'auto', display: 'flex' }}>
        <Box
          sx={{
            width: { xs: '100%', md: 480 },
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: { xs: 2, md: 3 },
          }}
        >
          {[
            { dot: '#1B7A4F', label: 'Available now' },
            { dot: '#D4694E', label: 'On the roadmap' },
          ].map((l) => (
            <Box key={l.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: l.dot }} />
              <Typography sx={{ fontFamily: fam, fontSize: '0.76rem', fontWeight: 500, color: alpha(ink, 0.6) }}>
                {l.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
}
